import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useLayoutEffect,
  useId,
} from "react";
import { generateInterlockingPiecePaths } from "./generateInterlockingPiecePaths";
import { createSquarePieces } from "./jigsawPieceGenerators";

/**
 * Derangement: Fisher-Yates shuffle that guarantees NO element stays at its
 * original index.  For n ≥ 2 a random permutation is a derangement ~37% of
 * the time, so we just retry.
 */
function derangement(n, rng) {
  if (n <= 1) return [0];
  for (;;) {
    const a = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    if (a.every((v, i) => v !== i)) return a;
  }
}

const SOLVED_REVEAL_DELAY_MS = 850;
const SOLVED_FULL_IMAGE_HOLD_MS = 5000;

const PLACE_GRADIENT_MS = 560;
const PLACE_SCALE_MS = 480;
const LOOSE_PIECE_SCALE = 1;
const PLACE_SCALE_FROM = LOOSE_PIECE_SCALE;
const DRAG_PLACE_THRESHOLD = 4;

/** Empty slot: muted patch — no puzzle image preview (darker than before for contrast). */
const EMPTY_SLOT_FILL = "#c8cad2";
/** Interlock grid: stroke on empty slots. */
const EMPTY_SLOT_STROKE_INTERLOCK = "#8e9199";

/** Piece silhouette: subtle dark edge reads richer than pure white on light art. */
const PIECE_EDGE_STROKE = "rgba(18, 20, 28, 0.28)";

/** Loose / draggable pieces: dimmer + grayscale (slightly dark shade). */
const LOOSE_PIECE_OPACITY = 0.7;

function easeOutCubic(t) {
  const u = Math.min(1, Math.max(0, t));
  return 1 - (1 - u) ** 3;
}

function pieceIsCorrect(p) {
  return Math.abs(p.x - p.correctX) < 1 && Math.abs(p.y - p.correctY) < 1;
}

/** Classic and irregular use the “loose until placed” scale animation */
function useLoosePieceAnim(_puzzleType, variant) {
  return variant === "classic" || variant === "irregular";
}

function placementAnimValues(t0, useLoose, now) {
  const elapsed = now - t0;
  let scale = 1;
  if (useLoose && elapsed < PLACE_SCALE_MS) {
    scale =
      PLACE_SCALE_FROM +
      (1 - PLACE_SCALE_FROM) * easeOutCubic(elapsed / PLACE_SCALE_MS);
  }
  let gradOpacity = 0;
  if (elapsed < PLACE_GRADIENT_MS) {
    gradOpacity = 0.38 * (1 - easeOutCubic(elapsed / PLACE_GRADIENT_MS));
  }
  return { scale, gradOpacity };
}

function hitTestGrid(px, py, list, cw, ch) {
  const pad = Math.min(cw, ch) * 0.22;
  for (let i = list.length - 1; i >= 0; i--) {
    const p = list[i];
    if (pieceIsCorrect(p)) continue;
    if (
      px >= p.x - pad &&
      px <= p.x + cw + pad &&
      py >= p.y - pad &&
      py <= p.y + ch + pad
    ) {
      return p;
    }
  }
  return null;
}

/**
 * SVG jigsaw: shuffled pieces, drag to snap/swap; empty slots show a light patch (no image).
 * @param {"square"|"interlock"} puzzleType
 * @param {"classic"|"square"|"irregular"} variant — used when puzzleType is interlock (irregular organic cuts)
 */
export default function FreeformJigsawSvg({
  imageUrl,
  rows,
  cols,
  puzzleType = "interlock",
  variant = "classic",
  boardW,
  boardH,
  onComplete,
  onPuzzleSolved,
  /** Called with count of pieces currently in the correct slot (for timeout thank-you copy). */
  onCorrectPieceCountChange,
}) {
  const reactId = useId().replace(/:/g, "");
  const svgRef = useRef(null);
  const completedRef = useRef(false);
  const dragRef = useRef(null);
  const rafRef = useRef(null);
  const pendingPtr = useRef(null);
  const piecesRef = useRef([]);
  const pathPiecesRef = useRef([]);

  const sessionSeed = useRef(Date.now() ^ (Math.random() * 0xffffffff));

  const pathPieces = useMemo(() => {
    const seedStr = `${imageUrl || ""}|${sessionSeed.current}`;
    if (puzzleType === "square") {
      return createSquarePieces(rows, cols);
    }
    const v =
      puzzleType === "interlock" && variant === "irregular"
        ? "irregular"
        : "classic";
    return generateInterlockingPiecePaths(rows, cols, seedStr, v);
  }, [puzzleType, boardW, boardH, rows, cols, imageUrl, variant]);

  pathPiecesRef.current = pathPieces;

  const svgW = boardW;

  const cw = boardW / Math.max(1, cols);
  const ch = boardH / Math.max(1, rows);

  const looseAnim = useLoosePieceAnim(puzzleType, variant);

  const [pieces, setPieces] = useState([]);
  const [zOrder, setZOrder] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const [renderTick, setRenderTick] = useState(0);
  const [showFullImageReveal, setShowFullImageReveal] = useState(false);
  const placeAnimStartRef = useRef(new Map());

  piecesRef.current = pieces;

  useLayoutEffect(() => {
    pieces.forEach((p) => {
      if (pieceIsCorrect(p) && !placeAnimStartRef.current.has(p.id)) {
        placeAnimStartRef.current.set(p.id, performance.now());
      }
      if (!pieceIsCorrect(p)) placeAnimStartRef.current.delete(p.id);
    });
  }, [pieces]);

  useEffect(() => {
    let raf;
    let cancelled = false;
    const loop = () => {
      if (cancelled) return;
      const now = performance.now();
      let active = false;
      placeAnimStartRef.current.forEach((t0) => {
        if (now - t0 < Math.max(PLACE_GRADIENT_MS, PLACE_SCALE_MS) + 100) {
          active = true;
        }
      });
      setRenderTick((n) => n + 1);
      if (active) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [pieces]);

  /* ---- initialise: deranged shuffle on slot grid ---- */
  useEffect(() => {
    if (!boardW || !boardH || !pathPieces.length || !imageUrl) return;

    const defs = pathPiecesRef.current;
    const n = defs.length;

    const rng = (() => {
      let s = 2166136261 ^ sessionSeed.current;
      const str = `${imageUrl}|${rows}|${cols}|${puzzleType}`;
      for (let i = 0; i < str.length; i++) {
        s ^= str.charCodeAt(i);
        s = Math.imul(s, 16777619);
      }
      return () => {
        s ^= s << 13;
        s ^= s >>> 17;
        s ^= s << 5;
        return (s >>> 0) / 4294967296;
      };
    })();
    const order = derangement(n, rng);
    const pieceToSlot = new Array(n);
    for (let slot = 0; slot < n; slot++) pieceToSlot[order[slot]] = slot;

    const next = defs.map((p) => {
      const slot = pieceToSlot[p.id];
      const slotCol = slot % cols;
      const slotRow = Math.floor(slot / cols);
      return {
        ...p,
        correctX: p.col * cw,
        correctY: p.row * ch,
        x: slotCol * cw,
        y: slotRow * ch,
      };
    });

    setPieces(next);
    setZOrder(next.map((p) => p.id));
    completedRef.current = false;
    setShowFullImageReveal(false);
  }, [pathPieces, boardW, boardH, cw, ch, imageUrl, rows, cols, puzzleType]);

  useEffect(() => {
    if (!pieces.length) return;
    const allCorrect = pieces.every((p) => pieceIsCorrect(p));
    if (allCorrect && !completedRef.current) {
      completedRef.current = true;
      onPuzzleSolved?.();
      const tReveal = window.setTimeout(() => {
        setShowFullImageReveal(true);
      }, SOLVED_REVEAL_DELAY_MS);
      const tDone = window.setTimeout(() => {
        onComplete?.();
      }, SOLVED_REVEAL_DELAY_MS + SOLVED_FULL_IMAGE_HOLD_MS);
      return () => {
        window.clearTimeout(tReveal);
        window.clearTimeout(tDone);
      };
    }
  }, [pieces, onComplete, onPuzzleSolved]);

  useEffect(() => {
    if (!onCorrectPieceCountChange || !pieces.length) return;
    const n = pieces.reduce((acc, p) => acc + (pieceIsCorrect(p) ? 1 : 0), 0);
    onCorrectPieceCountChange(n);
  }, [pieces, onCorrectPieceCountChange]);

  const toBoardCoords = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (ctm) {
      try {
        const p = pt.matrixTransform(ctm.inverse());
        return { x: p.x, y: p.y };
      } catch {
        /* fall through */
      }
    }
    const r = svg.getBoundingClientRect();
    const vb = svg.viewBox?.baseVal;
    const vw = vb?.width || boardW;
    const vh = vb?.height || boardH;
    if (!r.width || !r.height) return null;
    return {
      x: ((clientX - r.left) / r.width) * vw,
      y: ((clientY - r.top) / r.height) * vh,
    };
  }, [boardW, boardH]);

  const releasePointerCaptureSafe = useCallback(() => {
    const d = dragRef.current;
    if (!d?.pointerId || !svgRef.current) return;
    try {
      if (svgRef.current.hasPointerCapture(d.pointerId)) {
        svgRef.current.releasePointerCapture(d.pointerId);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const endDrag = useCallback(() => {
    if (dragRef.current == null) return;
    const { id, startX, startY, moved } = dragRef.current;
    releasePointerCaptureSafe();
    dragRef.current = null;
    setDraggingId(null);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const last = pendingPtr.current;
    pendingPtr.current = null;

    if (!moved) {
      return;
    }

    setPieces((prev) => {
      const dragged = prev.find((q) => q.id === id);
      if (!dragged) return prev;

      const curX = last ? last.x : dragged.x;
      const curY = last ? last.y : dragged.y;

      const snapCol = Math.max(0, Math.min(cols - 1, Math.round(curX / cw)));
      const snapRow = Math.max(0, Math.min(rows - 1, Math.round(curY / ch)));
      const snapX = snapCol * cw;
      const snapY = snapRow * ch;

      const occupant = prev.find(
        (q) =>
          q.id !== id &&
          Math.abs(q.x - snapX) < 1 &&
          Math.abs(q.y - snapY) < 1
      );

      if (occupant && pieceIsCorrect(occupant)) {
        return prev.map((q) =>
          q.id === id ? { ...q, x: startX, y: startY } : q
        );
      }

      return prev.map((q) => {
        if (q.id === id) return { ...q, x: snapX, y: snapY };
        if (occupant && q.id === occupant.id)
          return { ...q, x: startX, y: startY };
        return q;
      });
    });
  }, [cw, ch, cols, rows, releasePointerCaptureSafe]);

  const flushMove = useCallback(() => {
    rafRef.current = null;
    const pt = pendingPtr.current;
    if (!pt || dragRef.current == null) return;
    const { id } = dragRef.current;
    pendingPtr.current = null;
    setPieces((prev) =>
      prev.map((p) => (p.id === id ? { ...p, x: pt.x, y: pt.y } : p))
    );
  }, []);

  /** Left + right mouse both down during drag → cancel and return piece to pick-up cell */
  const revertActiveDrag = useCallback(() => {
    if (dragRef.current == null) return;
    const { id, startX, startY } = dragRef.current;
    releasePointerCaptureSafe();
    dragRef.current = null;
    setDraggingId(null);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pendingPtr.current = null;
    setPieces((prev) =>
      prev.map((p) => (p.id === id ? { ...p, x: startX, y: startY } : p))
    );
  }, [releasePointerCaptureSafe]);

  const scheduleMove = useCallback(
    (x, y) => {
      pendingPtr.current = { x, y };
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(flushMove);
    },
    [flushMove]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (dragRef.current == null) return;
      if (
        e.pointerType === "mouse" &&
        (e.buttons & 1) !== 0 &&
        (e.buttons & 2) !== 0
      ) {
        e.preventDefault();
        revertActiveDrag();
        return;
      }
      const board = toBoardCoords(e.clientX, e.clientY);
      if (!board) return;
      const drag = dragRef.current;
      const nextX = board.x - drag.grabX;
      const nextY = board.y - drag.grabY;
      if (!drag.moved) {
        const dx = nextX - drag.startX;
        const dy = nextY - drag.startY;
        if (dx * dx + dy * dy < DRAG_PLACE_THRESHOLD * DRAG_PLACE_THRESHOLD) {
          return;
        }
        drag.moved = true;
      }
      scheduleMove(nextX, nextY);
    };
    const onUp = () => endDrag();

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [toBoardCoords, scheduleMove, endDrag, revertActiveDrag, releasePointerCaptureSafe]);

  const onBoardPointerDown = useCallback(
    (e) => {
      if (!piecesRef.current.length) return;
      const board = toBoardCoords(e.clientX, e.clientY);
      if (!board) return;
      const ordered = zOrder
        .map((zid) => piecesRef.current.find((p) => p.id === zid))
        .filter(Boolean);
      const hit = hitTestGrid(board.x, board.y, ordered, cw, ch);
      if (!hit) return;
      e.preventDefault();
      const pointerId = e.pointerId;
      dragRef.current = {
        id: hit.id,
        grabX: board.x - hit.x,
        grabY: board.y - hit.y,
        startX: hit.x,
        startY: hit.y,
        moved: false,
        pointerId,
      };
      try {
        svgRef.current?.setPointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      setDraggingId(hit.id);
      setZOrder((zo) => [...zo.filter((zid) => zid !== hit.id), hit.id]);
    },
    [zOrder, cw, ch, toBoardCoords]
  );

  const onPiecePointerDown = useCallback(
    (e, p) => {
      e.stopPropagation();
      if (pieceIsCorrect(p)) return;
      const board = toBoardCoords(e.clientX, e.clientY);
      if (!board) return;
      const pointerId = e.pointerId;
      dragRef.current = {
        id: p.id,
        grabX: board.x - p.x,
        grabY: board.y - p.y,
        startX: p.x,
        startY: p.y,
        moved: false,
        pointerId,
      };
      try {
        svgRef.current?.setPointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      setDraggingId(p.id);
      setZOrder((zo) => [...zo.filter((zid) => zid !== p.id), p.id]);
    },
    [toBoardCoords]
  );

  if (!boardW || !boardH || !pieces.length) {
    return (
      <div className="jigsaw-puzzle-loading">
        <div className="quiz-loader" />
      </div>
    );
  }

  const orderedPieces = zOrder
    .map((zid) => pieces.find((p) => p.id === zid))
    .filter(Boolean);

  const strokeRefGrid = 1.15 / Math.min(cw, ch);

  return (
    <svg
      ref={svgRef}
      className="freeform-jigsaw-svg"
      width={svgW}
      height={boardH}
      viewBox={`0 0 ${svgW} ${boardH}`}
      style={{ overflow: "visible" }}
      role="application"
      aria-label="Jigsaw puzzle"
    >
      <defs>
        <filter id={`jf-loose-piece-gray-${reactId}`}>
          <feColorMatrix
            type="matrix"
            values="0.32 0.32 0.32 0 0.02
                    0.32 0.32 0.32 0 0.02
                    0.32 0.32 0.32 0 0.02
                    0 0 0 1 0"
          />
        </filter>
        <linearGradient
          id={`jf-flash-${reactId}`}
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.55" />
          <stop offset="40%" stopColor="#fffef0" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
        {pathPieces.map((p) => (
          <clipPath
            key={`cp-def-${p.id}`}
            id={`cp-${reactId}-${p.id}`}
            clipPathUnits="userSpaceOnUse"
          >
            <path d={p.pathD} />
          </clipPath>
        ))}
      </defs>

      <rect
        x={0}
        y={0}
        width={boardW}
        height={boardH}
        className="freeform-jigsaw-board"
        fill="rgba(12, 14, 22, 0.07)"
        stroke="rgba(12, 14, 22, 0.18)"
        strokeWidth={1}
        onPointerDown={onBoardPointerDown}
      />

      <g
        className="freeform-jigsaw-slot-guides"
        pointerEvents="none"
        aria-hidden="true"
      >
        {pathPieces.map((p) => {
          const correctHere = pieces.some(
            (q) => q.id === p.id && pieceIsCorrect(q)
          );
          if (correctHere) return null;
          return (
            <g
              key={`slot-guide-${p.id}`}
              transform={`translate(${p.col * cw},${p.row * ch}) scale(${cw},${ch})`}
            >
              <path d={p.pathD} fill={EMPTY_SLOT_FILL} />
              <path
                d={p.pathD}
                fill="none"
                stroke={EMPTY_SLOT_STROKE_INTERLOCK}
                strokeWidth={strokeRefGrid}
                vectorEffect="nonScalingStroke"
              />
            </g>
          );
        })}
      </g>

      <g>
        {orderedPieces.map((p) => {
          void renderTick;
          const isDrag = draggingId === p.id;
          const isCorrect = pieceIsCorrect(p);
          const clipUid = `cp-${reactId}-${p.id}`;
          const t0 = placeAnimStartRef.current.get(p.id);
          const { scale: placeScale, gradOpacity } =
            isCorrect && t0 != null
              ? placementAnimValues(t0, looseAnim, performance.now())
              : { scale: 1, gradOpacity: 0 };

          const looseScale = !isCorrect && looseAnim ? LOOSE_PIECE_SCALE : 1;
          const innerScale =
            isCorrect && looseAnim && t0 != null ? placeScale : looseScale;
          const useScaleGroup =
            looseAnim &&
            (innerScale < 0.999 ||
              (!isCorrect && looseScale < 0.999) ||
              gradOpacity > 0.01);

          const loosePieceGrayId = `jf-loose-piece-gray-${reactId}`;

          const innerContent = (
            <>
              <g
                clipPath={`url(#${clipUid})`}
                opacity={isCorrect ? 1 : LOOSE_PIECE_OPACITY}
                filter={isCorrect ? undefined : `url(#${loosePieceGrayId})`}
              >
                <image
                  href={imageUrl}
                  x={-p.col}
                  y={-p.row}
                  width={cols}
                  height={rows}
                  preserveAspectRatio="none"
                />
              </g>
              {gradOpacity > 0.01 && (
                <path
                  d={p.pathD}
                  fill={`url(#jf-flash-${reactId})`}
                  fillOpacity={gradOpacity}
                  style={{ pointerEvents: "none" }}
                />
              )}
              <path
                d={p.pathD}
                fill="none"
                stroke={PIECE_EDGE_STROKE}
                strokeWidth={strokeRefGrid}
                vectorEffect="nonScalingStroke"
                style={{ pointerEvents: "none" }}
              />
            </>
          );

          const scaledInner = useScaleGroup ? (
            <g
              transform={`translate(0.5,0.5) scale(${innerScale}) translate(-0.5,-0.5)`}
            >
              {innerContent}
            </g>
          ) : (
            innerContent
          );

          return (
            <g
              key={p.id}
              transform={`translate(${p.x},${p.y}) scale(${cw},${ch})`}
              style={{
                cursor: isCorrect ? "default" : "grab",
                touchAction: "none",
                pointerEvents: isCorrect ? "none" : "auto",
              }}
              onPointerDown={
                isCorrect ? undefined : (ev) => onPiecePointerDown(ev, p)
              }
            >
              {isDrag && !looseAnim ? (
                <g transform="translate(0.5,0.5) scale(1.05) translate(-0.5,-0.5)">
                  {scaledInner}
                </g>
              ) : (
                scaledInner
              )}
            </g>
          );
        })}
      </g>

      {showFullImageReveal && imageUrl ? (
        <g
          className="freeform-jigsaw-solved-reveal"
          pointerEvents="all"
          style={{ pointerEvents: "all" }}
        >
          <rect x={0} y={0} width={boardW} height={boardH} fill="#e8eaef" />
          <image
            href={imageUrl}
            x={0}
            y={0}
            width={boardW}
            height={boardH}
            preserveAspectRatio="none"
          />
        </g>
      ) : null}
    </svg>
  );
}
